import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { getDuration, Inscription, useReliableInterval } from '@common-fe/common-fe';
import _ from 'lodash';

import useSendVerificationQuery, { VerificationType } from '@/modules/employee/Employee/PersonalInformation/GeneralInfo/queries/useSendVerification.query';
import resendVerificationStore from '@/modules/employee/Employee/PersonalInformation/GeneralInfo/stores/resendVerification.store';
import { getTimeRemaining } from '@/utils/modifiers';
import { convertSecondsToMinutes } from '@/utils/modifiers/convertSecondsToMinutes';

enum VerificationServerError {
  EMAIL_ALREADY_VERIFIED = 'Email is already verified',
}

interface Props {
  valueKey: string;
  value: string;
  employeeId: string;
  setVerificationErrorText: (value: string) => void;
}

const ResendVerificationLink: React.FC<Props> = ({ valueKey, value, employeeId, setVerificationErrorText }) => {
  const { timestamps, resetTimestamp, setSendTimestamp } = resendVerificationStore();
  const { sendVerification, isLoading } = useSendVerificationQuery();
  const [remainingTime, setRemainingTime] = useState(0);

  const handleResend = useCallback(async () => {
    try {
      await sendVerification({
        type: VerificationType.EMAIL,
        isLink: true,
        employeeId,
        value,
      });
  
      setSendTimestamp(valueKey,  new Date().getTime());
    } catch (error) {
      const errorMessage = _.get(error, 'response.data.elevate_error_message', '') as string;
      if (errorMessage.includes(VerificationServerError.EMAIL_ALREADY_VERIFIED)) {
        setVerificationErrorText('This email address has been just verified. Refresh the page.');
      }
    }

  }, [sendVerification, setSendTimestamp, setVerificationErrorText, valueKey, value, employeeId]);

  const isDisabled = useMemo(() => isLoading || remainingTime > 0, [isLoading, remainingTime]);
  const defaultRemainingTime = useMemo(() => getTimeRemaining(timestamps?.[valueKey], getDuration({minutes: 1}), getDuration({ seconds: 1})),
    [timestamps, valueKey]);

  const updateRemainingTime = useCallback((time: number) => {
    const currentRemainingTime = time - 1;
    if (currentRemainingTime <= 0) {
      resetTimestamp(valueKey);
    }
    return currentRemainingTime;
  }, [valueKey, resetTimestamp]);

  const { start, clear } =  useReliableInterval(() => {
    if (defaultRemainingTime > 0) {
      setRemainingTime(updateRemainingTime);
    } 
  },getDuration({seconds: 1}));

  useEffect(() => {
    if (defaultRemainingTime > 0) {
      start();
  
      return () => {
        clear();
      };
    } else {
      resetTimestamp(valueKey);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultRemainingTime, updateRemainingTime]);

  useLayoutEffect(() => {
    setRemainingTime(defaultRemainingTime);
  }, [defaultRemainingTime]);

  return (
    <Inscription
      color={isDisabled ? 'textDisabled' : 'textAccent'}
      {...(isDisabled ? {} : { onClick: () => handleResend(), cursor: 'pointer' })}
    >
      {'Resend verification '}
      {remainingTime > 0 && `(${convertSecondsToMinutes(remainingTime)})`}
    </Inscription>
  );
};

export default ResendVerificationLink;
